<template>
  <ExternalPage :show-nav="true">
    <div class="max-w-3xl mx-auto p-6 space-y-8">
      <header>
        <h1 class="text-2xl font-bold text-gray-800">Connect to Claude</h1>
        <p class="mt-2 text-gray-600">
          Generate a personal token to use Aerocle from Claude. The token acts as
          <span class="font-semibold">you</span> — keep it private, and revoke it
          here if a device is lost.
        </p>
      </header>

      <!-- Generate -->
      <section class="rounded-lg border border-gray-200 p-5">
        <h2 class="text-lg font-semibold text-gray-800 mb-3">Generate a token</h2>
        <div class="flex items-end gap-3">
          <div class="flex-1">
            <label class="block text-sm text-gray-600 mb-1">
              Label (e.g. a device name, so you can tell tokens apart)
            </label>
            <input
              v-model="newLabel"
              type="text"
              placeholder="ThinkPad"
              class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring focus:border-accent-primary"
              @keyup.enter="generate"
            />
          </div>
          <base-button-affirmative :disabled="generating" @click="generate">
            {{ generating ? "Generating…" : "Generate" }}
          </base-button-affirmative>
        </div>

        <!-- Freshly-minted token: shown ONCE -->
        <div v-if="freshToken" class="mt-4 rounded-md bg-amber-50 border border-amber-200 p-4">
          <p class="text-sm font-semibold text-amber-800">
            Copy this now — you won't be able to see it again.
          </p>
          <div class="mt-2 flex items-center gap-2">
            <code class="flex-1 break-all rounded bg-white border border-amber-200 px-3 py-2 text-sm">
              {{ freshToken }}
            </code>
            <base-button-affirmative @click="copyToken">Copy</base-button-affirmative>
          </div>
        </div>
      </section>

      <!-- Existing tokens -->
      <section class="rounded-lg border border-gray-200 p-5">
        <h2 class="text-lg font-semibold text-gray-800 mb-3">Your tokens</h2>
        <p v-if="!tokens.length" class="text-gray-500 text-sm">No tokens yet.</p>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-left text-gray-500 border-b">
              <th class="py-2 pr-4">Label</th>
              <th class="py-2 pr-4">Created</th>
              <th class="py-2 pr-4">Expires</th>
              <th class="py-2"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in tokens" :key="t.id" class="border-b last:border-0">
              <td class="py-2 pr-4">{{ t.label || "—" }}</td>
              <td class="py-2 pr-4">{{ formatDate(t.created_at) }}</td>
              <td class="py-2 pr-4">{{ t.expiry ? formatDate(t.expiry) : "—" }}</td>
              <td class="py-2 text-right">
                <base-button-negative @click="revoke(t)">Revoke</base-button-negative>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- How to use -->
      <section class="rounded-lg border border-gray-200 p-5 text-sm text-gray-600 space-y-1">
        <h2 class="text-lg font-semibold text-gray-800 mb-2">How to use it</h2>
        <p>1. Generate a token above and copy it.</p>
        <p>2. In Claude Desktop, open <span class="font-semibold">Settings → Extensions</span> and install the <span class="font-semibold">Aerocle</span> connector.</p>
        <p>3. Paste this token into the connector's token field and save. Claude can now use Aerocle as you.</p>
        <p class="text-gray-500">Tokens auto-renew while in use and lapse after ~24h idle — just generate a new one if that happens.</p>
      </section>
    </div>
  </ExternalPage>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import ExternalPage from "../external/base/ExternalPage.vue";
import { listMCPTokens, mintMCPToken, revokeMCPToken } from "@/services/MCPToken";

const store = useStore();
const $toast = useToast();
const authToken = computed(() => store.getters["auth/showToken"]);

const tokens = ref([]);
const newLabel = ref("");
const freshToken = ref(null);
const generating = ref(false);

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d) ? value : d.toLocaleString();
}

async function fetchTokens() {
  const res = await listMCPTokens(authToken.value);
  if (res?.status === 200) {
    tokens.value = res.data;
  } else {
    $toast.error("Couldn't load your tokens.");
  }
}

async function generate() {
  if (generating.value) return;
  generating.value = true;
  const res = await mintMCPToken(authToken.value, newLabel.value.trim() || null);
  generating.value = false;
  if (res?.status === 201) {
    freshToken.value = res.data.token;
    newLabel.value = "";
    $toast.success("Token generated.");
    fetchTokens();
  } else {
    $toast.error("Couldn't generate a token.");
  }
}

async function copyToken() {
  try {
    await navigator.clipboard.writeText(freshToken.value);
    $toast.success("Token copied to clipboard.");
  } catch (e) {
    $toast.error("Couldn't copy — select and copy manually.");
  }
}

async function revoke(t) {
  if (!window.confirm(`Revoke token "${t.label || t.id}"? Any Claude connection using it will stop working.`)) return;
  const res = await revokeMCPToken(authToken.value, t.id);
  if (res?.status === 204) {
    $toast.success("Token revoked.");
    fetchTokens();
  } else {
    $toast.error("Couldn't revoke the token.");
  }
}

onMounted(fetchTokens);
</script>
